import classNames from 'classnames';
import * as React from 'react';

import { PictureProfile } from '../../../../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../../../../Resources/Translations';
import { ProductAvailabilityMode } from '../../../../../Components/Availability/ProductAvailabilityMode.csharp';
import { Loader } from '../../../../../Components/Loader/Loader';
import { CompactFavouriteProductButton } from '../../../../../Customers/Favourites/Components/FavouritesButton/FavouriteProductButton';
import { unique } from '../../../../../ReactComponents/OnlyUnique';
import { errorAdditionalInfo } from '../../../../../ReactComponents/QuantityUtils';
import { ResponsivePicture } from '../../../../../ReactComponents/ResponsivePicture/ResponsivePicture';
import { GoogleTagManager } from '../../../../../Tracking/GoogleTagManager/GoogleTagManager';
import { handleClickEvents } from '../../../../../Tracking/GoogleTagManager/HandleClickEvents';
import { SetQuantityResult } from '../../../../Checkout/Summary/LineItemsApi';
import { CartItemValidationIssueViewModel } from '../../ViewModels/CartItemValidationIssueViewModel.csharp';
import { CartItemViewModel } from '../../ViewModels/CartItemViewModel.csharp';
import ItemQuantityInput from '../ItemQuantityInput/ItemQuantityInput';
import { CartItemPrice } from './CartItemPrice';
import { CheckoutItemPrice } from './CheckoutItemPrice';

interface CartItemProps {
    item: CartItemViewModel;
    scrollToItem?: (scrollPosition: number) => void;
    setQuantity?: (productCode: string, quantity: number) => Promise<SetQuantityResult>;
    removeFromCart?: (productCode: string) => Promise<boolean>;
    pictureProfile: PictureProfile;
    resetLastUpdatedItem?: () => void;
    lastUpdatedItem?: string;
    hideItemAvailability?: boolean;
    showQuantity?: boolean;
    showFavouriteButton?: boolean;
    favouritesUrl?: string;
    priceLayout?: 'cart' | 'checkout';
}

export class CartItem extends React.Component<CartItemProps, { item: CartItemViewModel; isQuantityLoaderActive: boolean }> {
    private readonly cartItemRef = React.createRef<HTMLLIElement>();
    private areControlsActive = () => this.props.setQuantity && this.props.removeFromCart;

    constructor(props: CartItemProps) {
        super(props);
        this.state = {
            item: { ...props.item },
            isQuantityLoaderActive: false
        };
    }

    componentDidUpdate(prevProps: CartItemProps) {
        if (this.props.lastUpdatedItem === this.state.item.code) {
            this.autoScroll();
        }

        if (prevProps.item !== this.props.item) {
            this.setState({
                item: { ...this.props.item }
            });
        }
    }

    render() {
        const {
            state: { item, isQuantityLoaderActive }
        } = this;
        const rxNameIframeUrl = item.rxProductNameUrl && item.rxProductNameUrl + (this.props.hideItemAvailability ? '?hideStatus=true' : '?hideStatus=false');
        const showCancelledMessage = item.quantityCancelled > 0;

        return (
            <li ref={this.cartItemRef} className={this.getClassNames(item)} key={item.nameInfo.additionalName} onClick={this.clearHighlightedItem}>
                <div className="CartItem__Inner">
                    <div className="CartItem__Image">
                        {this.renderImage(item)}
                        {item.isCooled && <div className="CartItemImage--CooledProductIndicator"></div>}
                    </div>
                    <div className="CartItem__Information">
                        <div className="CartItem__Description">
                            <div>
                                {this.renderWithLink(
                                    item.isRx && item.shouldDisplayRxIframes ? (
                                        <div className="CartItem__IframeWrapper">
                                            <span className="CartItem__RxHeading">{getTranslation(keys['cartSummary.prescription'])}</span>
                                            <iframe src={rxNameIframeUrl} scrolling="no" sandbox="allow-same-origin" />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="CartItem__Header">{item.nameInfo.mainName}</div>
                                            <div className="CartItem__Subheader">{item.nameInfo.additionalName}</div>
                                        </>
                                    )
                                )}
                            </div>
                            {this.props.item.powerReviewsReviewUrl && (
                                <div className="CartItem__ReviewThisProduct">
                                    <a href={this.props.item.powerReviewsReviewUrl}>{getTranslation(keys['orderSummary.rateThisProduct'])}</a>
                                </div>
                            )}
                            {this.renderAvailabilityIndicator()}
                            {this.props.showFavouriteButton && this.props.favouritesUrl && !item.isRx && (
                                <CompactFavouriteProductButton
                                    favouritesUrl={this.props.favouritesUrl}
                                    product={item}
                                    isAddedToFavourites={item.isAddedToFavourites}
                                />
                            )}
                        </div>
                        <div className={classNames('CartItem__QuantityWrapper', showCancelledMessage && 'CartItem__QuantityWrapper--WithMessage')}>
                            {showCancelledMessage && (
                                <div className="CartItem__CancelledMessage">
                                    <div className="CartItem__MessageWrapper">
                                        <div className="CartItem__CancelledIcon">!</div>
                                        <span>
                                            {item.quantityCancelled === item.quantityOrdered
                                                ? getTranslation(keys['orderSummary.productCancelled'])
                                                : `${item.quantityCancelled} ${getTranslation(keys['orderSummary.of'])} ${
                                                      item.quantityOrdered
                                                  } ${getTranslation(keys['orderSummary.cancelledProducts'])}`}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {this.props.setQuantity && (
                                <div className="CartItem__Quantity">
                                    <Loader isLoading={isQuantityLoaderActive} className="CartItem__PriceLoader" />

                                    <ItemQuantityInput
                                        item={item}
                                        setQuantity={this.props.setQuantity}
                                        isDisabled={!item.canChangeQuantity || this.state.isQuantityLoaderActive || item.isRx}
                                        onRequestTimeoutStart={() => this.setState({ isQuantityLoaderActive: true })}
                                        onRequestTimeoutEnd={() => this.setState({ isQuantityLoaderActive: false })}
                                    />
                                </div>
                            )}
                            {this.props.priceLayout != 'checkout' ? (
                                <CartItemPrice item={item} showQuantity={this.props.showQuantity} isQuantityLoaderActive={isQuantityLoaderActive} />
                            ) : (
                                <CheckoutItemPrice item={item} showQuantity={this.props.showQuantity} isQuantityLoaderActive={isQuantityLoaderActive} />
                            )}
                        </div>
                    </div>
                    {this.props.setQuantity && (
                        <button onClick={() => this.removeItem(item.code)} className="CartItem__Remove">
                            <span className="visuallyHidden">{getTranslation(keys['cartSummary.remove'])}</span>
                        </button>
                    )}
                </div>

                {!!item.validationIssues && item.validationIssues.length > 0 ? (
                    <ul className="CartItem__Message">
                        {item.validationIssues
                            .map((issue) => this.getErrorMessage(issue))
                            .filter(unique)
                            .map((issue, i) => (
                                <li key={i}>{issue}</li>
                            ))}
                    </ul>
                ) : (
                    this.props.lastUpdatedItem === item.code && (
                        <ul className="CartItem__Message">
                            <li>{getTranslation(keys['cartSummary.addToCartMessage'])}</li>
                        </ul>
                    )
                )}
            </li>
        );
    }

    private renderAvailabilityIndicator = () => {
        const { item, hideItemAvailability } = this.props;
        if (hideItemAvailability || item.isRx) return <></>;

        const isAvailable = this.areControlsActive() && item.availability && item.availability.availabilityMode === ProductAvailabilityMode.InStock;
        const availabilityLabel = item.availability ? getTranslation(`productAvailability.${ProductAvailabilityMode[item.availability.availabilityMode]}`) : '';

        if (isAvailable && !item.availability.isAvailableQuantityLow) {
            return <span className="CartItem__Availability">{availabilityLabel}</span>;
        } else if (isAvailable && item.availability.isAvailableQuantityLow) {
            return <span className="CartItem__Availability CartItem__Availability--Low">{getTranslation('productAvailability.LowInStock')}</span>;
        } else {
            return <span className="CartItem__Availability CartItem__Availability--NotInStock">{availabilityLabel}</span>;
        }
    };

    private renderImage = (item: CartItemViewModel) => {
        return item.isRx && item.shouldDisplayRxIframes
            ? this.renderWithLink(<iframe src={item.rxProductImageUrl} scrolling="no" sandbox="allow-same-origin" />)
            : item.image && this.renderWithLink(<ResponsivePicture model={item.image.picture} profile={this.props.pictureProfile} isLazyLoaded />);
    };

    private getClassNames = (item: CartItemViewModel) => {
        return classNames(
            'CartItem',
            this.props.lastUpdatedItem === item.code && 'CartItem--LastUpdated',
            item.validationIssues?.length > 0 && (item.validationIssues.filter((x) => !x.isWarning).length > 0 ? 'CartItem--Error' : 'CartItem--Info')
        );
    };

    private renderWithLink = (content: JSX.Element) => {
        const { item } = this.props;

        return item.isAvailable ? (
            <a href={item.link} {...handleClickEvents((e) => GoogleTagManager.cartItemClick(e, item))}>
                {content}
            </a>
        ) : (
            <div>{content}</div>
        );
    };

    private getErrorMessage = (error: CartItemValidationIssueViewModel) => {
        const { minQuantity, maxQuantity } = this.props.item;

        return getTranslation(`cartSummary.validation.${error.code}`) + errorAdditionalInfo(error.code, maxQuantity, minQuantity);
    };

    private clearHighlightedItem = () => {
        this.props.resetLastUpdatedItem && this.props.resetLastUpdatedItem();
    };

    private removeItem = (code: string) => {
        if (this.props.removeFromCart) {
            this.props.removeFromCart(code).then((succeeded) => {
                if (succeeded) {
                    GoogleTagManager.removeFromCart(this.props.item, this.props.item.quantityOrdered);
                }
            });
        }
    };

    private autoScroll = () => {
        const cartItem = this.cartItemRef.current;
        const mobileMediaBreakpoint = 520;
        const headerHeight = 140;
        const headerHeightMobile = 110;

        const offsetY = window.innerWidth >= mobileMediaBreakpoint ? headerHeight : headerHeightMobile;

        if (cartItem) {
            this.props.scrollToItem && this.props.scrollToItem(cartItem.offsetTop - offsetY);
        }
    };
}

export default CartItem;
